/* TerraLife — Parcours débutant (sections/terralife-parcours-pack.liquid).
   La taille du terrarium fixe la quantité de chaque produit ; les cases
   retirent ce qu'on a déjà ; le total suit ; « Ajouter le pack » met les
   produits disponibles et cochés au panier en une fois (le nom du pack est
   noté sur la commande), puis mène au panier. Pack complet et intact
   (rien de décoché, rien en rupture, quantités prévues) : il part en un seul
   lot Shopify (scripts/sync_parcours_lots.py), au prix arrondi au « ,90 » (barré
   seulement pendant une promo) ;
   l'espèce choisie et les compléments restent des lignes à part. Sur téléphone,
   une barre reprend total et bouton quand le bouton principal sort de l'écran. */
(() => {
  /** Cents in the shop's money format (e.g. "€{{amount_with_comma_separator}}"). */
  const formatMoney = (section, cents) => {
    // Apps may wrap the format in HTML (e.g. a translation app's <span>): text only.
    const format = (section.dataset.moneyFormat || '€{{amount_with_comma_separator}}').replace(
      /<[^>]*>/g,
      '',
    );
    const [int, dec] = (cents / 100).toFixed(2).split('.');
    const grouped = (sep) => int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const values = {
      amount: `${grouped(',')}.${dec}`,
      amount_no_decimals: grouped(','),
      amount_with_comma_separator: `${grouped('.')},${dec}`,
      amount_no_decimals_with_comma_separator: grouped('.'),
      amount_with_space_separator: `${grouped(' ')},${dec}`,
      amount_with_apostrophe_separator: `${grouped("'")}.${dec}`,
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => values[key] ?? values.amount);
  };

  const ticked = (el) => el.querySelector('[data-pack-toggle]')?.checked !== false;
  const qtyOf = (el) => Number(el.dataset.qty) || 0;

  /** The pack's available products, ticked and wanted for this size. */
  const chosen = (section) =>
    [...section.querySelectorAll('[data-pack-item]')].filter((el) => ticked(el) && qtyOf(el) > 0);

  /** A line of the pack itself: not a complement, not the species sold apart. */
  const inLot = (el) => !el.classList.contains('is-option') && !el.hasAttribute('data-pack-apart');

  const lotOf = (section) => {
    try {
      return JSON.parse(section.querySelector('[data-pack-lot]')?.textContent || 'null');
    } catch {
      return null;
    }
  };

  /**
   * The lot variant when the pack is complete and untouched for the chosen size: every line
   * ticked, none sold out, at the planned quantity. Else null (the lines go one by one).
   */
  const fullLot = (section) => {
    const lot = lotOf(section);
    if (!lot) return null;
    const size = Number(section.querySelector('[data-pack-size]:checked')?.value || 0);
    const variant = lot.variants?.[size];
    const plan = lot.plan?.sizes?.[size];
    if (!variant?.available || !plan) return null;
    const outs = [...section.querySelectorAll('[data-pack-out]')].filter(
      (el) => inLot(el) && !el.classList.contains('is-none'),
    );
    if (outs.length) return null;
    const lines = new Map();
    for (const el of section.querySelectorAll('[data-pack-item]')) {
      if (!inLot(el)) continue;
      const qty = qtyOf(el);
      if (qty === 0 && Number(el.dataset.wanted || 0) === 0) continue;
      if (!ticked(el)) return null;
      lines.set(el.dataset.variant, (lines.get(el.dataset.variant) || 0) + qty);
    }
    if (lines.size !== plan.length) return null;
    return plan.every((l) => lines.get(String(l.v)) === l.q) ? variant : null;
  };

  /** The quantity planned for a line, never above its stock (with a word when cut). */
  const cap = (el) => {
    const wanted = Number(el.dataset.wanted ?? el.dataset.qty) || 0;
    const stock = el.dataset.stock === undefined ? Infinity : Number(el.dataset.stock);
    const qty = Math.min(wanted, stock);
    el.dataset.qty = String(qty);
    const short = el.querySelector('[data-pack-short]');
    if (short) {
      short.hidden = !(wanted > qty && qty > 0);
      short.textContent = short.hidden ? '' : `Plus que ${qty} en stock : quantité ajustée`;
    }
  };

  /** Quantities follow the chosen terrarium size, never above the stock. */
  const applySize = (section, index) => {
    section.querySelectorAll('[data-pack-item]').forEach((el) => {
      const qtys = (el.dataset.qtys || '').split(',').filter((q) => q !== '');
      if (qtys.length <= index) return;
      el.dataset.wanted = String(Number(qtys[index]) || 0);
      cap(el);
    });
    // Sold-out lines are not added, but show what this size would need.
    section.querySelectorAll('[data-pack-out]').forEach((el) => {
      const qtys = (el.dataset.qtys || '').split(',').filter((q) => q !== '');
      if (qtys.length <= index) return;
      const qty = Number(qtys[index]) || 0;
      const shown = el.querySelector('[data-pack-qty]');
      if (shown) shown.textContent = `× ${qty}`;
      const line = el.querySelector('[data-pack-line]');
      if (line) line.textContent = formatMoney(section, qty * Number(el.dataset.unit || 0));
      el.classList.toggle('is-none', qty === 0);
    });
  };

  /** Another species (or colour) chosen: the line becomes that product, at its price and stock. */
  const applyChoice = (opt) => {
    const el = opt.closest('[data-pack-item]');
    if (!el) return;
    el.dataset.variant = opt.value;
    el.dataset.unit = opt.dataset.unit || '0';
    if (opt.dataset.stock === '' || opt.dataset.stock === undefined) delete el.dataset.stock;
    else el.dataset.stock = opt.dataset.stock;
    const name = el.querySelector('.tlp-pack__name');
    if (name && opt.dataset.title) {
      name.textContent = opt.dataset.short || opt.dataset.title;
      name.title = opt.dataset.title;
      name.href = opt.dataset.url || name.href;
    }
    const thumb = el.querySelector('.tlp-pack__thumb');
    if (thumb && opt.dataset.url) thumb.href = opt.dataset.url;
    const img = thumb?.querySelector('img');
    if (img && opt.dataset.image) {
      img.src = opt.dataset.image;
      img.removeAttribute('srcset');
    }
    const wish = el.querySelector('button-wishlist');
    if (wish && opt.dataset.product) wish.dataset.productId = opt.dataset.product.split('/').pop();
    const current = el.querySelector('[data-choice-current]');
    if (current && opt.dataset.short) current.textContent = opt.dataset.short;
    // The list of species closes once one is picked.
    const box = el.querySelector('[data-choice-box]');
    if (box) box.open = false;
    cap(el);
  };

  /** Lines, total, count and buttons follow the ticks and the size. */
  const refresh = (section) => {
    let cents = 0;
    let count = 0;
    section.querySelectorAll('[data-pack-item]').forEach((el) => {
      const qty = qtyOf(el);
      const line = qty * Number(el.dataset.unit || 0);
      el.querySelector('[data-pack-qty]').textContent = `× ${qty}`;
      el.querySelector('[data-pack-line]').textContent = formatMoney(section, line);
      // Hidden only when this terrarium size needs none (never for a lack of stock).
      el.classList.toggle('is-none', Number(el.dataset.wanted ?? qty) === 0);
      el.classList.toggle('is-off', !ticked(el));
      if (ticked(el) && qty > 0) {
        cents += line;
        count += qty;
      }
    });
    // Complete pack: the lot's price (the separate prices rounded down to « ,90 »). Struck
    // through only in a promo, when the lot carries a compare-at price.
    const lot = fullLot(section);
    let deal = '';
    if (lot) {
      const packCents = [...section.querySelectorAll('[data-pack-item]')]
        .filter((el) => inLot(el) && ticked(el))
        .reduce((sum, el) => sum + qtyOf(el) * Number(el.dataset.unit || 0), 0);
      const rest = cents - packCents;
      if (lot.compare > lot.price) deal = formatMoney(section, rest + lot.compare);
      cents = rest + lot.price;
    }
    section.querySelectorAll('[data-pack-total]').forEach((t) => {
      t.textContent = formatMoney(section, cents);
      if (deal) {
        const was = document.createElement('s');
        was.className = 'tlp-pack__was';
        was.textContent = deal;
        t.prepend(was, ' ');
      }
    });
    section.querySelector('[data-pack-count]').textContent =
      `${count} article${count > 1 ? 's' : ''}`;
    section.querySelectorAll('[data-pack-add]').forEach((b) => {
      b.disabled = count === 0;
    });
  };

  document.addEventListener('change', (event) => {
    const section = event.target.closest('.tlp-pack');
    if (!section) return;
    if (event.target.matches('[data-pack-size]')) applySize(section, Number(event.target.value));
    if (event.target.matches('[data-pack-choice]')) applyChoice(event.target);
    if (event.target.matches('[data-pack-size], [data-pack-toggle], [data-pack-choice]'))
      refresh(section);
  });

  const add = async (button) => {
    const section = button.closest('.tlp-pack');
    const status = section.querySelector('[data-pack-status]');
    // Each line carries the pack's name (hidden property, leading underscore): the admin
    // app counts the sales made through each parcours from the orders.
    const packName = section.dataset.packName || document.title;
    const line = (el) => ({
      id: Number(el.dataset.variant),
      quantity: qtyOf(el),
      properties: { _parcours: packName },
    });
    // The complete pack: one lot; the species and the complements beside it.
    const lot = fullLot(section);
    const items = lot
      ? [
          { id: lot.id, quantity: 1, properties: { _parcours: packName } },
          ...chosen(section)
            .filter((el) => !inLot(el))
            .map(line),
        ]
      : chosen(section).map(line);
    if (!items.length) return;
    // The parcours' printed tutorial, free: on the order, it tells the team to print it.
    // Added once, even if the pack is added again.
    const tuto = Number(section.dataset.tutoVariant || 0);
    if (tuto) {
      const root0 = window.Shopify?.routes?.root || '/';
      const inCart = await fetch(`${root0}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((c) => c.items.some((i) => i.variant_id === tuto))
        .catch(() => false);
      if (!inCart) items.push({ id: tuto, quantity: 1, properties: { _parcours: packName } });
    }
    const buttons = section.querySelectorAll('[data-pack-add]');
    const labels = [...buttons].map((b) => b.textContent);
    buttons.forEach((b) => {
      b.disabled = true;
      b.textContent = 'Ajout en cours…';
    });
    status.textContent = '';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // Shopify may add part of the pack (stock) and still answer an error:
        // if anything of the pack is in the cart, show the cart, not a false failure.
        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .catch(() => null);
        const ids = new Set(items.map((i) => i.id));
        if (!cart?.items?.some((i) => ids.has(i.variant_id))) {
          throw new Error(err.description || err.message || "Le pack n'a pas pu être ajouté.");
        }
      }
      window.location.href = `${root}cart`;
    } catch (e) {
      status.textContent = e.message;
      buttons.forEach((b, i) => {
        b.disabled = false;
        b.textContent = labels[i];
      });
    }
  };

  // The species list is a dropdown: a click elsewhere or Escape closes it.
  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-choice-box][open]').forEach((box) => {
      if (!box.contains(event.target)) box.open = false;
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-choice-box][open]').forEach((box) => {
      box.open = false;
      box.querySelector('summary')?.focus();
    });
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-pack-add]');
    if (button) {
      event.preventDefault();
      add(button);
    }
  });

  /**
   * Phones: the bar shows once the main buy block has been scrolled past
   * (checked on scroll, so a jump past it — anchor, fast fling — counts too).
   */
  const watchBar = (section) => {
    const bar = section.querySelector('[data-pack-bar]');
    const buy = section.querySelector('[data-pack-buy]');
    if (!bar || !buy) return;
    const phone = window.matchMedia('(max-width: 989px)');
    let queued = false;
    const update = () => {
      queued = false;
      const passed = buy.getBoundingClientRect().bottom < 0;
      const inSection = section.getBoundingClientRect().bottom > window.innerHeight * 0.5;
      bar.hidden = !(phone.matches && passed && inSection);
      // The theme's own bottom bar (Accueil, Compte…) shows on some scrolls: sit above it.
      const nav = document.querySelector('mobile-navigation-bar');
      const navUp = nav && !nav.classList.contains('invisible') && nav.offsetHeight > 0;
      bar.style.bottom = navUp ? `${nav.offsetHeight}px` : '0px';
    };
    const queue = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  };

  const init = () =>
    document.querySelectorAll('.tlp-pack').forEach((section) => {
      watchBar(section);
      refresh(section);
    });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
